import React, { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { X, Package, Layers, Warehouse, History, Image as ImageIcon, Printer, Box } from 'lucide-react';
import LabelPrintView from './LabelPrintView';
import IndividualItemManagerModal from './IndividualItemManagerModal';
import inventoryService from '../services/inventoryService';

const TABS = [
  { id: 'dados', label: 'Dados Gerais', icon: Package },
  { id: 'variantes', label: 'Variantes', icon: Layers },
  { id: 'estoque', label: 'Estoque', icon: Warehouse },
  { id: 'historico', label: 'Histórico', icon: History }
];

const ProductDetailsModal = ({ productId, productName, isOpen, onClose, onViewStock }) => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('dados');
  const [showLabelModal, setShowLabelModal] = useState(false);
  const [selectedStockEntry, setSelectedStockEntry] = useState(null);
  const [showItemManagerModal, setShowItemManagerModal] = useState(false);
  const [itemManagerVariant, setItemManagerVariant] = useState(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['inventory-product-details', productId],
    queryFn: () => inventoryService.getProductDetails(productId),
    enabled: isOpen && !!productId
  });

  useEffect(() => {
    if (isOpen) {
      setActiveTab('dados');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const product = data?.data;
  const stockSummary = product?.estoque_por_armazem || [];
  const totalStock = stockSummary.reduce((acc, w) => acc + (parseFloat(w.estoque_total) || 0), 0);
  const totalReserved = stockSummary.reduce((acc, w) => acc + (parseFloat(w.estoque_reservado) || 0), 0);
  const totalAvailable = stockSummary.reduce((acc, w) => acc + (parseFloat(w.estoque_disponivel) || 0), 0);

  const formatQuantity = (value) => {
    const num = parseFloat(value) || 0;
    return num.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 3 });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTipoMovimento = (tipo) => {
    const tipos = {
      'IN': { label: 'Entrada', color: 'bg-green-100 text-green-800' },
      'OUT': { label: 'Saída', color: 'bg-red-100 text-red-800' },
      'TRANSFER': { label: 'Transferência', color: 'bg-blue-100 text-blue-800' },
      'AJUSTE': { label: 'Ajuste', color: 'bg-yellow-100 text-yellow-800' }
    };
    return tipos[tipo] || { label: tipo, color: 'bg-gray-100 text-gray-800' };
  };

  const handleViewStockByWarehouse = () => {
    if (onViewStock) {
      onViewStock(productId, productName);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-4 sm:p-6 border-b bg-gray-50 flex-shrink-0">
          <div className="flex justify-between items-start">
            <div className="min-w-0 flex-1">
              <h2 className="text-lg sm:text-xl font-bold text-gray-900 truncate pr-4">
                {productName}
              </h2>
              {product && (
                <div className="flex flex-wrap items-center gap-2 mt-1">
                  <span className="text-sm text-gray-500">SKU: {product.sku || '-'}</span>
                  <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${
                    product.ativo ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {product.ativo ? 'Ativo' : 'Inativo'}
                  </span>
                </div>
              )}
            </div>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 p-1 flex-shrink-0"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 mt-4 overflow-x-auto">
            {TABS.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg whitespace-nowrap transition ${
                    activeTab === tab.id
                      ? 'bg-primary-100 text-primary-700'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          {isLoading && (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
              Erro ao carregar detalhes do produto.
            </div>
          )}

          {product && activeTab === 'dados' && (
            <div className="space-y-6">
              {/* Image */}
              {product.imagem_url && (
                <div className="flex justify-center">
                  <div className="w-48 h-48 rounded-lg overflow-hidden bg-gray-100 border">
                    <img
                      src={product.imagem_url}
                      alt={product.nome}
                      className="w-full h-full object-contain"
                    />
                  </div>
                </div>
              )}

              {/* Basic Info Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-500">Categoria</label>
                  <p className="text-gray-900">{product.categoria_nome || '-'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">Marca</label>
                  <p className="text-gray-900">{product.marca_nome || '-'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">Unidade</label>
                  <p className="text-gray-900">{product.unidade_sigla || '-'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">Código Interno</label>
                  <p className="text-gray-900">{product.codigo_interno || '-'}</p>
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Descrição</label>
                <p className="text-gray-900 whitespace-pre-wrap">
                  {product.descricao || 'Sem descrição'}
                </p>
              </div>

              {/* Timestamps */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4 border-t">
                <div>
                  <label className="block text-sm font-medium text-gray-500">Criado em</label>
                  <p className="text-gray-900">{formatDate(product.created_at)}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">Atualizado em</label>
                  <p className="text-gray-900">{formatDate(product.updated_at)}</p>
                </div>
              </div>
            </div>
          )}

          {product && activeTab === 'variantes' && (
            <div>
              {product.variantes && product.variantes.length > 0 ? (
                <div className="overflow-x-auto -mx-4 sm:mx-0">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">SKU</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden sm:table-cell">Cor</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden md:table-cell">Tamanho</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Custo</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Venda</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Estoque</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Ações</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {product.variantes.map((variant) => (
                        <tr key={variant.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {variant.sku_variacao || '-'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900 hidden sm:table-cell">
                            {variant.cor || '-'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900 hidden md:table-cell">
                            {variant.tamanho_label || variant.medida_text || '-'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900 text-right">
                            {formatCurrency(variant.preco_custo)}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900 text-right">
                            {formatCurrency(variant.preco_venda)}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900 text-right">
                            <span className={parseFloat(variant.estoque_disponivel || 0) <= 0 ? 'text-red-600' : ''}>
                              {formatQuantity(variant.estoque_disponivel)}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex items-center justify-end gap-1.5">
                              <button
                                type="button"
                                onClick={() => {
                                  setItemManagerVariant(variant);
                                  setShowItemManagerModal(true);
                                }}
                                className="px-2 py-1 bg-amber-50 hover:bg-amber-100 text-amber-800 rounded-lg text-xs font-bold border border-amber-200 flex items-center gap-1 transition-colors shadow-2xs"
                                title="Gerenciar cada peça física / corte / pedaço individualmente"
                              >
                                <Box className="w-3.5 h-3.5 text-amber-600" />
                                <span>Peças</span>
                              </button>
                              {variant.estoque_disponivel > 0 && (
                                <button
                                  onClick={() => {
                                    setSelectedStockEntry({
                                      produto_id: productId,
                                      produto_nome: productName,
                                      variante_id: variant.id,
                                      cor: variant.cor,
                                      tamanho_label: variant.tamanho_label,
                                      quantidade: variant.estoque_disponivel
                                    });
                                    setShowLabelModal(true);
                                  }}
                                  className="text-primary-600 hover:text-primary-800 p-1 border border-primary-200 rounded-lg hover:bg-primary-50"
                                  title="Imprimir Etiquetas"
                                >
                                  <Printer className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  Nenhuma variante cadastrada
                </div>
              )}
            </div>
          )}

          {product && activeTab === 'estoque' && (
            <div className="space-y-4">
              {/* Summary Cards */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-blue-50 rounded-lg p-4">
                  <p className="text-sm font-medium text-blue-600">Total</p>
                  <p className="text-2xl font-bold text-blue-900">{formatQuantity(totalStock)}</p>
                </div>
                <div className="bg-yellow-50 rounded-lg p-4">
                  <p className="text-sm font-medium text-yellow-600">Reservado</p>
                  <p className="text-2xl font-bold text-yellow-900">{formatQuantity(totalReserved)}</p>
                </div>
                <div className="bg-green-50 rounded-lg p-4">
                  <p className="text-sm font-medium text-green-600">Disponível</p>
                  <p className="text-2xl font-bold text-green-900">{formatQuantity(totalAvailable)}</p>
                </div>
              </div>

              {/* Stock by Warehouse Summary */}
              {stockSummary.length > 0 ? (
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <h3 className="text-sm font-medium text-gray-700">Estoque por Armazém</h3>
                    <button
                      onClick={handleViewStockByWarehouse}
                      className="text-sm text-primary-600 hover:text-primary-800 font-medium"
                    >
                      Ver detalhes →
                    </button>
                  </div>
                  <div className="overflow-x-auto -mx-4 sm:mx-0">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Armazém</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Reservado</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Disponível</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {stockSummary.map((warehouse) => (
                          <tr key={warehouse.armazem_id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm font-medium text-gray-900">
                              {warehouse.armazem_nome}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900 text-right">
                              {formatQuantity(warehouse.estoque_total)}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900 text-right">
                              {formatQuantity(warehouse.estoque_reservado)}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900 text-right">
                              {formatQuantity(warehouse.estoque_disponivel)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  Nenhum estoque cadastrado
                </div>
              )}
            </div>
          )}

          {product && activeTab === 'historico' && (
            <div>
              {product.movimentos_recentes && product.movimentos_recentes.length > 0 ? (
                <div className="overflow-x-auto -mx-4 sm:mx-0">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Data</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden sm:table-cell">Variante</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Qtd</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden md:table-cell">Observações</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {product.movimentos_recentes.map((mov) => {
                        const tipoInfo = getTipoMovimento(mov.tipo);
                        return (
                          <tr key={mov.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                              {formatDate(mov.data_mov)}
                            </td>
                            <td className="px-4 py-3 text-sm">
                              <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${tipoInfo.color}`}>
                                {tipoInfo.label}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900 hidden sm:table-cell">
                              {mov.sku_variacao || '-'}
                              {mov.cor && ` (${mov.cor})`}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900 text-right font-medium">
                              {mov.quantidade > 0 ? '+' : ''}{mov.quantidade.toLocaleString('pt-BR')}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-500 hidden md:table-cell max-w-xs truncate">
                              {mov.observacoes || '-'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  Nenhum movimento registrado
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t bg-gray-50 flex justify-end flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
          >
            Fechar
          </button>
        </div>

        {showLabelModal && selectedStockEntry && (
          <LabelPrintView
            stockEntry={selectedStockEntry}
            onClose={() => {
              setShowLabelModal(false);
              setSelectedStockEntry(null);
            }}
          />
        )}

        {showItemManagerModal && itemManagerVariant && (
          <IndividualItemManagerModal
            variant={itemManagerVariant}
            product={product || { id: productId, nome: productName }}
            isOpen={showItemManagerModal}
            onClose={() => {
              setShowItemManagerModal(false);
              setItemManagerVariant(null);
            }}
            onStockUpdated={() => {
              queryClient.invalidateQueries(['inventory-product-details', productId]);
            }}
          />
        )}
      </div>
    </div>
  );
};

export default ProductDetailsModal;
